import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Leaf, Send, Loader2, Sparkles, Heart, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import "../styles/emergence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmergenceChat() {
  const { i18n } = useTranslation();
  const isFrench = i18n.language === 'fr';
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Message d'accueil
    const welcomeMessage = {
      role: "assistant",
      content: isFrench 
        ? "Bonjour 🌱\n\nJe suis ton coach intérieur, ici pour t'accompagner avec bienveillance dans cette période de changement.\n\nComment te sens-tu aujourd'hui ? Qu'est-ce qui occupe ton esprit en ce moment ?"
        : "Hello 🌱\n\nI'm your inner coach, here to support you kindly through this period of change.\n\nHow are you feeling today? What's on your mind right now?"
    };
    setMessages([welcomeMessage]);
  }, [isFrench]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: "user", content: input.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/emergence/chat`, {
        session_id: sessionId,
        message: input.trim(),
        language: i18n.language,
        history: messages.slice(-10) // Garder les 10 derniers messages pour le contexte
      });

      const assistantMessage = { role: "assistant", content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage = {
        role: "assistant",
        content: isFrench 
          ? "Je suis désolé, une erreur est survenue. Peux-tu réessayer ?"
          : "I'm sorry, an error occurred. Can you try again?"
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestedPrompts = isFrench ? [
    "Je me sens perdu(e) dans ma vie professionnelle",
    "J'ai du mal à prendre une décision importante",
    "Je manque d'énergie et de motivation",
    "J'ai peur de l'échec"
  ] : [
    "I feel lost in my professional life",
    "I'm struggling to make an important decision",
    "I lack energy and motivation",
    "I'm afraid of failure"
  ];

  const handleSuggestedPrompt = (prompt) => {
    setInput(prompt);
  };

  const resetChat = () => {
    const welcomeMessage = {
      role: "assistant",
      content: isFrench 
        ? "Nouvelle conversation commencée 🌱\n\nComment puis-je t'aider aujourd'hui ?"
        : "New conversation started 🌱\n\nHow can I help you today?"
    };
    setMessages([welcomeMessage]);
  };

  return (
    <div className="emergence-bg min-h-screen flex flex-col">
      {/* Header */}
      <header className="emergence-glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/emergence" className="flex items-center gap-2 text-[#3B7A8C] hover:opacity-80" data-testid="back-link">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{isFrench ? "Retour" : "Back"}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#D4A574]" />
            <span className="font-heading text-lg text-[#2D5A66]">{isFrench ? "Coach intérieur" : "Inner Coach"}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={resetChat}
            className="text-[#7A8B99] hover:text-[#3B7A8C]"
            data-testid="reset-chat-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {isFrench ? "Nouveau" : "New"}
          </Button>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 pt-24 pb-32 px-4 overflow-y-auto">
        <div className="max-w-2xl mx-auto space-y-4">
          {messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`emergence-message-animate ${msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}`}
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              <div className={msg.role === 'user' ? 'emergence-chat-user' : 'emergence-chat-ai'}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2 text-[#3B7A8C]">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-xs font-medium">{isFrench ? "Coach" : "Coach"}</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="emergence-chat-ai">
                <div className="emergence-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions - affichées seulement au début */}
        {messages.length <= 1 && (
          <div className="max-w-2xl mx-auto mt-8">
            <p className="text-sm text-[#7A8B99] mb-3 text-center">
              {isFrench ? "Tu peux commencer par..." : "You can start with..."}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {suggestedPrompts.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestedPrompt(prompt)}
                  className="px-4 py-2 text-sm rounded-full bg-white border border-[#3B7A8C]/20 text-[#3D4F5F] hover:border-[#3B7A8C] hover:bg-[#3B7A8C]/5 transition-all"
                  data-testid={`suggestion-${idx}`}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Input */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-[#3B7A8C]/10 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isFrench ? "Écris ton message..." : "Write your message..."}
            className="emergence-chat-input flex-1"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="emergence-btn-primary h-12 w-12 p-0 rounded-full"
            data-testid="send-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
        <p className="text-center text-xs text-[#7A8B99] mt-2">
          {isFrench 
            ? "Je suis là pour t'écouter et t'accompagner avec bienveillance 💚"
            : "I'm here to listen and support you with kindness 💚"}
        </p>
      </footer>
    </div>
  );
}
